import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _class, _descriptor, _LayerGroup;
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { Layer } from "abreact/incubator/ui/layer/Layer";
import { LayerRoot } from "abreact/incubator/ui/layer/LayerRoot";
import { LayerManager } from "abreact/incubator/ui/manager/LayerManager";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ElementUtils } from "abweb/utils/ElementUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { observable, action, makeObservable } from "mobx";
import * as ReactDOM from "react-dom";
import * as React from "react";
/**
 * The layer manager implementation that controls the set of HTML layers shown over the current page.
 */
export let LayerGroup = (_dec = observable.shallow, _class = (_LayerGroup = class LayerGroup {
  static hasDefaultAppRoot() {
    return document.getElementById(LayerGroup.DEFAULT_APP_ROOT_ID) !== null;
  }
  constructor(id = LayerManager.DEFAULT_GROUP_ID, appRootElement, layerRootElement) {
    _defineProperty(this, "zIndex", -1);
    _defineProperty(this, "id", void 0);
    _defineProperty(this, "layerRoot", void 0);
    _defineProperty(this, "appRootElement", void 0);
    _defineProperty(this, "layerRootElement", void 0);
    // Maps a Layer ID to the set of event handlers that layer registered
    _defineProperty(this, "eventHandlers", new Map());
    _initializerDefineProperty(this, "layers", _descriptor, this);
    makeObservable(this);
    this.layers = [];
    this.id = id;
    const retrievedAppRoot = appRootElement || document.getElementById(LayerGroup.DEFAULT_APP_ROOT_ID);
    if (!retrievedAppRoot) {
      throw Error("Failed to recover application root");
    }
    this.appRootElement = retrievedAppRoot;
    this.layerRootElement = layerRootElement || this.createLayerRootElement();
    if (!ReactUtils.hasClassName(this.layerRootElement, LayerGroup.LAYER_ROOT_CLASSNAME)) {
      this.addLayerRootClass(LayerGroup.LAYER_ROOT_CLASSNAME);
    }
    this.layerRoot = ReactDOM.render(/*#__PURE__*/React.createElement(LayerRoot, {
      layerGroup: this
    }), this.layerRootElement);
  }
  get topmostLayer() {
    return this.layers.length > 0 ? this.layers[this.layers.length - 1] : null;
  }

  /**
   * Push a new node or Layer as the topmost layer.
   */
  push(node, id) {
    let layer;
    if (ClassUtils.isInstanceOf(node, Layer)) {
      layer = node;
      if (layer.layerGroupId !== this.id) {
        throw new Error(`Layer's layerGroupId "${layer.layerGroupId}" doesn't match this LayerGroup's id "${this.id}"`);
      }
      if (id && layer.id !== id) {
        throw new Error(`Layer's id "${layer.id}" doesn't match the given id "${id}".`);
      }
    } else {
      layer = new Layer(this.id, node, id);
    }
    this.layers.push(layer);
    this.layerRootElement.classList.add("visible");
    return layer;
  }

  /**
   * Returns the layer for the specified id.
   */
  getLayer(id) {
    for (let layer of this.layers) {
      if (layer.id === id) {
        return layer;
      }
    }
    return undefined;
  }

  /**
   * Remove the topmost layer from the layer manager.
   *
   * @deprecated in favor of the close method on the popup handle.
   */
  pop() {
    return this.removeIndex(this.layers.length - 1);
  }

  /**
   * Removes a layer from the layer group. Does nothing if ID or layer is not found.
   *
   * @param layer is either a layer object or a layer ID
   */
  remove(layer) {
    let foundLayer;
    if (ClassUtils.isInstanceOf(layer, Layer)) {
      foundLayer = layer;
    } else {
      foundLayer = this.getLayer(layer);
    }
    if (foundLayer === undefined) {
      LogUtils.getLogger(__filename).error(`Failed to remove layer handle ${layer} as it was not found`);
    } else {
      return this.removeIndex(this.layers.indexOf(foundLayer));
    }
    return false;
  }

  /**
   * Removes the layer at the given index. Will do nothing on an invalid index.
   * @param index
   */
  removeIndex(index) {
    if (this.layers.length > index && index >= 0) {
      this.layers.splice(index, 1);
      // If this was the final layer, then re-enable the application
      if (this.layers.length === 0) {
        // Hide the layer root
        this.layerRootElement.classList.remove("visible");
      }
      return true;
    }
    return false;
  }

  /**
   * Remove all layers from the layer manager.
   *
   * This is primarily useful when navigating via browser history.
   */
  removeAll() {
    if (this.layers.length > 0) {
      this.layers.splice(0, this.layers.length);
      // Hide the layer root
      this.layerRootElement.classList.remove("visible");
    }
  }

  /**
   * Destroy the layer manager (mostly useful for testing)
   */
  destroy() {
    this.removeAll();
    this.removeLayerRoot();
  }
  createLayerRootElement() {
    const doc = this.appRootElement.ownerDocument;
    let newElement = doc.createElement("div");
    newElement.id = `${this.id}-root`;
    newElement.className = LayerGroup.LAYER_ROOT_CLASSNAME;
    this.setZindex(newElement);
    this.appRootElement.parentElement.appendChild(newElement);
    return newElement;
  }
  addLayerRootClass(classString) {
    this.layerRootElement.classList.add(classString);
  }
  setZindex(div) {
    this.zIndex = ElementUtils.getCssAsNumber(LayerManager.idToCssVariable(this.id), 5000, this.appRootElement);
    div.style.zIndex = this.zIndex.toString();
  }

  /**
   * Removes the layer root if it has been mounted.
   */
  removeLayerRoot() {
    ReactDOM.unmountComponentAtNode(this.layerRootElement);
  }
  getEventHandlers(layer) {
    return this.eventHandlers.get(layer.id);
  }
  registerEventHandlers(layer, callBacks) {
    this.eventHandlers.set(layer.id, callBacks);
  }
  unregisterEventHandlers(layer) {
    this.eventHandlers.delete(layer.id);
  }
}, _defineProperty(_LayerGroup, "DEFAULT_APP_ROOT_ID", "appGoesHere"), _defineProperty(_LayerGroup, "LAYER_ROOT_CLASSNAME", "ab-layer-root"), _LayerGroup), _descriptor = _applyDecoratedDescriptor(_class.prototype, "layers", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "push", [action], Object.getOwnPropertyDescriptor(_class.prototype, "push"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "remove", [action], Object.getOwnPropertyDescriptor(_class.prototype, "remove"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeIndex", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeIndex"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeAll"), _class.prototype), _class);
//# sourceMappingURL=LayerGroup.js.map
